import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import api from '../utils/api';
import LoadingSpinner from '../components/LoadingSpinner';
import CountUp from '../components/CountUp';
import { ArrowTopRightOnSquareIcon, CalendarDaysIcon, Cog6ToothIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../context/AuthContext';

// Platform URL patterns - accepts full URL or username
const platformUrls: Record<string, (value: string) => string> = {
  dartcounter: (v) => v.startsWith('http') ? v : `https://dartcounter.net/@${encodeURIComponent(v)}`,
  lidarts: (v) => v.startsWith('http') ? v : `https://lidarts.org/profile/${encodeURIComponent(v)}`,
  nakka: (v) => v.startsWith('http') ? v : `https://n01.darts-scores.com/player/${encodeURIComponent(v)}`,
  prodarter: (v) => v.startsWith('http') ? v : `https://www.pro-darter.com/player-ede3.php?playername=${encodeURIComponent(v)}`,
};

const API_BASE = (import.meta.env.VITE_API_URL || 'http://localhost:3002/api').replace('/api', '');

interface ProfileData {
  userId: string;
  profile: {
    displayName: string;
    alternateName: string | null;
    bio: string | null;
    profileImage: string | null;
    bannerUrl: string | null;
    timezone: string;
    layout: 'single' | 'tabbed';
    stats: {
      average: number | null;
      oneEighties: number | null;
      bestLeg: number | null;
      highestCheckout: number | null;
    };
    equipment: {
      dartsBrand: string | null;
      dartsWeight: string | null;
    };
    social: {
      facebook: string | null;
      discord: string | null;
      tiktok: string | null;
      youtube: string | null;
      twitter: string | null;
      instagram: string | null;
    };
    dartcounterUrl: string | null;
    platforms: {
      webcam: {
        dartcounter: string | null;
        wda: string | null;
        lidarts: string | null;
        nakka: string | null;
        prodarter: string | null;
        dartconnect: string | null;
        godartspro: string | null;
      };
      autoScoring: {
        scolia: boolean;
        autodarts: boolean;
        targetomni: boolean;
      };
    };
  };
  leagues: Array<{
    name: string;
    abbreviation: string | null;
    logoUrl: string | null;
    websiteUrl: string | null;
    facebookUrl: string | null;
    discordUrl: string | null;
    whatsappUrl: string | null;
    isActive: boolean;
  }>;
  gameStats: {
    wins: number;
    losses: number;
    draws: number;
  };
  availableSlots: Array<{ start: string; end: string }>;
  upcomingGames: Array<{
    opponentName: string;
    leagueName: string | null;
    legs: number;
    scheduledStart: string;
  }>;
  recentResults: Array<{
    opponentName: string;
    leagueName: string | null;
    date: string;
    ownerLegsWon: number;
    opponentLegsWon: number;
    winner: string;
  }>;
  highlights: Array<{
    id: string;
    type: '180' | 'high_checkout' | 'best_leg' | 'ton_plus' | '9_darter' | 'other';
    value: number;
    description: string | null;
    imageUrl: string | null;
    videoUrl: string | null;
    achievedAt: string | null;
    platform: string | null;
    isVerified: boolean;
    displayOrder: number;
  }>;
}

export default function PublicProfile() {
  const { slug } = useParams<{ slug: string }>();
  const { user, isAuthenticated } = useAuth();
  const isOwnProfile = isAuthenticated && user?.profileSlug === slug;
  const [data, setData] = useState<ProfileData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeSection, setActiveSection] = useState('about');

  // Friendship state
  const [isFriend, setIsFriend] = useState(false);
  const [friendshipChecked, setFriendshipChecked] = useState(false);

  // Check friendship status when profile loads
  useEffect(() => {
    const checkFriendship = async () => {
      if (!isAuthenticated || !data?.userId || isOwnProfile) {
        setFriendshipChecked(true);
        return;
      }
      try {
        const response = await api.get('/friends');
        const friendIds = response.data.friends.map((f: { friend_id: string }) => f.friend_id);
        setIsFriend(friendIds.includes(data.userId));
      } catch {
        // Ignore errors
      }
      setFriendshipChecked(true);
    };
    checkFriendship();
  }, [isAuthenticated, data?.userId, isOwnProfile]);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await api.get(`/profile/${slug}`);
        setData(response.data);
      } catch {
        setError('Profile not found');
      } finally {
        setIsLoading(false);
      }
    };

    if (slug) fetchProfile();
  }, [slug]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#101022]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#101022] px-4">
        <div className="card max-w-md w-full text-center">
          <div className="text-4xl mb-4">:(</div>
          <p className="text-gray-400">{error || 'Profile not found'}</p>
        </div>
      </div>
    );
  }

  const { profile, leagues, gameStats, availableSlots, upcomingGames, recentResults, highlights } = data;

  const getHighlightTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      '180': '180',
      'high_checkout': 'High Checkout',
      'best_leg': 'Best Leg',
      'ton_plus': '100+ Score',
      '9_darter': '9 Darter',
      'other': 'Achievement'
    };
    return labels[type] || type;
  };

  // Group slots by date
  const slotsByDate: Record<string, Array<{ start: string; end: string }>> = {};
  availableSlots.slice(0, 50).forEach((slot) => {
    const dateKey = format(parseISO(slot.start), 'yyyy-MM-dd');
    if (!slotsByDate[dateKey]) {
      slotsByDate[dateKey] = [];
    }
    slotsByDate[dateKey].push(slot);
  });

  return (
    <div className="min-h-screen">
      {/* Owner Action Bar - shown when viewing your own profile */}
      {isOwnProfile && (
        <div className="sticky top-0 z-50 bg-[#1111d4] text-white">
          <div className="max-w-4xl mx-auto px-4 py-2 flex items-center justify-between">
            <span className="text-sm">Viewing your profile</span>
            <div className="flex gap-2">
              <Link
                to="/dashboard"
                className="px-3 py-1 bg-white/20 hover:bg-white/30 rounded text-sm transition-colors"
              >
                Dashboard
              </Link>
              <Link
                to="/my-profile"
                className="px-3 py-1 bg-white/20 hover:bg-white/30 rounded text-sm transition-colors flex items-center gap-1"
              >
                <Cog6ToothIcon className="w-4 h-4" />
                Edit Profile
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Banner Section */}
      {profile.bannerUrl && (
        <div className="bg-[#0a0a18] py-6 relative z-10">
          <div className="max-w-2xl mx-auto px-4">
            <img
              src={profile.bannerUrl.startsWith('http') ? profile.bannerUrl : `${API_BASE}${profile.bannerUrl}`}
              alt={profile.displayName}
              className="w-full h-auto rounded-xl animate-banner-in"
            />
          </div>
        </div>
      )}

      {/* Navigation - only show for tabbed layout */}
      {profile.layout === 'tabbed' && (
        <nav className="sticky top-0 z-50 bg-[#0a0a18]/90 backdrop-blur-sm border-b border-[#2a2a4a]">
          <div className="max-w-4xl mx-auto px-4">
            <div className="flex items-center justify-between h-14">
              <span className="font-bold text-[#1111d4]">{profile.displayName}</span>
              <div className="flex gap-4 text-sm">
                <button
                  onClick={() => setActiveSection('about')}
                  className={`hover:text-[#1111d4] transition-colors ${activeSection === 'about' ? 'text-[#1111d4]' : 'text-gray-400'}`}
                >
                  About
                </button>
                <button
                  onClick={() => setActiveSection('schedule')}
                  className={`hover:text-[#1111d4] transition-colors ${activeSection === 'schedule' ? 'text-[#1111d4]' : 'text-gray-400'}`}
                >
                  Availability
                </button>
                <button
                  onClick={() => setActiveSection('leagues')}
                  className={`hover:text-[#1111d4] transition-colors ${activeSection === 'leagues' ? 'text-[#1111d4]' : 'text-gray-400'}`}
                >
                  Leagues
                </button>
                <button
                  onClick={() => setActiveSection('contact')}
                  className={`hover:text-[#1111d4] transition-colors ${activeSection === 'contact' ? 'text-[#1111d4]' : 'text-gray-400'}`}
                >
                  Book
                </button>
              </div>
            </div>
          </div>
        </nav>
      )}

      <div className="relative z-10 max-w-4xl mx-auto px-4 py-8 space-y-12">

        {/* Hero Section */}
        <section className="text-center">
          {profile.profileImage ? (
            <img
              src={profile.profileImage.startsWith('http') ? profile.profileImage : `${API_BASE}${profile.profileImage}`}
              alt={profile.displayName}
              className="w-32 h-32 mx-auto rounded-full object-cover border-4 border-[#1111d4] mb-4"
            />
          ) : (
            <div className="w-32 h-32 mx-auto rounded-full bg-[#1a1a3a] border-4 border-[#1111d4] flex items-center justify-center mb-4">
              <span className="text-4xl text-[#1111d4]">
                {profile.displayName.charAt(0).toUpperCase()}
              </span>
            </div>
          )}
          <h1 className="text-3xl font-bold text-white mb-1">{profile.displayName}</h1>
          {profile.alternateName && (
            <p className="text-gray-500 text-lg mb-2">{profile.alternateName}</p>
          )}
          {profile.bio && (
            <p className="text-gray-400 max-w-lg mx-auto">{profile.bio}</p>
          )}
        </section>

        {/* Sign up prompt for non-logged in users */}
        {!isAuthenticated && (
          <div className="card text-center">
            <p className="text-gray-400 mb-4">
              Want to book a game with {profile.displayName}?
            </p>
            <Link to="/register" className="btn-primary inline-block">
              Sign Up to Play
            </Link>
          </div>
        )}

        {/* About Section */}
        {(profile.layout === 'single' || activeSection === 'about') && (
          <section id="about" className="space-y-6">
            {/* Match Record */}
            <div className="card">
              <h2 className="text-lg font-semibold text-white mb-4">Match Record</h2>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <div className="text-3xl font-bold text-green-400">
                    <CountUp end={gameStats.wins} duration={1200} />
                  </div>
                  <div className="text-sm text-gray-400">Wins</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-red-400">
                    <CountUp end={gameStats.losses} duration={1200} />
                  </div>
                  <div className="text-sm text-gray-400">Losses</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-yellow-400">
                    <CountUp end={gameStats.draws} duration={1200} />
                  </div>
                  <div className="text-sm text-gray-400">Draws</div>
                </div>
              </div>
            </div>

            {/* Performance Stats */}
            {(profile.stats.average || profile.stats.oneEighties || profile.stats.bestLeg || profile.stats.highestCheckout) && (
              <div className="card">
                <h2 className="text-lg font-semibold text-white mb-4">Performance</h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                  {profile.stats.average && (
                    <div>
                      <div className="text-2xl font-bold text-[#1111d4]">
                        <CountUp end={profile.stats.average} duration={1500} decimals={2} />
                      </div>
                      <div className="text-sm text-gray-400">Average</div>
                    </div>
                  )}
                  {profile.stats.oneEighties !== null && (
                    <div>
                      <div className="text-2xl font-bold text-[#1111d4]">
                        <CountUp end={profile.stats.oneEighties} duration={1500} />
                      </div>
                      <div className="text-sm text-gray-400">180s</div>
                    </div>
                  )}
                  {profile.stats.bestLeg && (
                    <div>
                      <div className="text-2xl font-bold text-[#1111d4]">
                        <CountUp end={profile.stats.bestLeg} duration={1500} />
                      </div>
                      <div className="text-sm text-gray-400">Best Leg</div>
                    </div>
                  )}
                  {profile.stats.highestCheckout && (
                    <div>
                      <div className="text-2xl font-bold text-[#1111d4]">
                        <CountUp end={profile.stats.highestCheckout} duration={1500} />
                      </div>
                      <div className="text-sm text-gray-400">Highest Checkout</div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Top Scores & Highlights */}
            {highlights && highlights.length > 0 && (
              <div className="card">
                <h2 className="text-lg font-semibold text-white mb-4">Top Scores & Finishes</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {highlights.map((highlight) => (
                    <div key={highlight.id} className="bg-[#252550] rounded-lg overflow-hidden">
                      {highlight.imageUrl && (
                        <img
                          src={`${API_BASE}${highlight.imageUrl}`}
                          alt={getHighlightTypeLabel(highlight.type)}
                          className="w-full h-40 object-cover"
                        />
                      )}
                      <div className="p-4">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <span className="inline-block px-2 py-0.5 bg-[#1111d4]/20 text-[#1111d4] rounded text-xs font-medium mb-1">
                              {getHighlightTypeLabel(highlight.type)}
                            </span>
                            <p className="text-2xl font-bold text-white">{highlight.value}</p>
                          </div>
                          {highlight.isVerified && (
                            <span className="text-green-400" title="Verified">
                              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                              </svg>
                            </span>
                          )}
                        </div>
                        {highlight.description && (
                          <p className="text-sm text-gray-400 mb-2">{highlight.description}</p>
                        )}
                        <div className="flex flex-wrap gap-2 text-xs text-gray-500">
                          {highlight.platform && (
                            <span className="bg-[#1a1a3a] px-2 py-1 rounded">{highlight.platform}</span>
                          )}
                          {highlight.achievedAt && (
                            <span className="bg-[#1a1a3a] px-2 py-1 rounded">
                              {format(parseISO(highlight.achievedAt), 'MMM d, yyyy')}
                            </span>
                          )}
                          {highlight.videoUrl && (
                            <a
                              href={highlight.videoUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="bg-[#1a1a3a] px-2 py-1 rounded text-[#1111d4] hover:underline flex items-center gap-1"
                            >
                              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" />
                              </svg>
                              Watch
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Equipment */}
            {(profile.equipment.dartsBrand || profile.equipment.dartsWeight) && (
              <div className="card">
                <h2 className="text-lg font-semibold text-white mb-4">Equipment</h2>
                <div className="text-gray-300">
                  {profile.equipment.dartsBrand && <span>{profile.equipment.dartsBrand}</span>}
                  {profile.equipment.dartsBrand && profile.equipment.dartsWeight && <span> - </span>}
                  {profile.equipment.dartsWeight && <span>{profile.equipment.dartsWeight}</span>}
                </div>
              </div>
            )}

            {/* Social Links */}
            {(profile.social.facebook || profile.social.discord || profile.social.tiktok ||
              profile.social.youtube || profile.social.twitter || profile.social.instagram) && (
              <div className="card">
                <h2 className="text-lg font-semibold text-white mb-4">Connect</h2>
                <div className="flex flex-wrap gap-3">
                  {profile.social.facebook && (
                    <a href={profile.social.facebook} target="_blank" rel="noopener noreferrer"
                       className="px-4 py-2 bg-[#1877f2] text-white rounded-lg hover:opacity-80 transition-opacity">
                      Facebook
                    </a>
                  )}
                  {profile.social.discord && (
                    <span className="px-4 py-2 bg-[#5865f2] text-white rounded-lg">
                      {profile.social.discord}
                    </span>
                  )}
                  {profile.social.tiktok && (
                    <a href={profile.social.tiktok} target="_blank" rel="noopener noreferrer"
                       className="px-4 py-2 bg-black text-white rounded-lg hover:opacity-80 transition-opacity border border-gray-700">
                      TikTok
                    </a>
                  )}
                  {profile.social.youtube && (
                    <a href={profile.social.youtube} target="_blank" rel="noopener noreferrer"
                       className="px-4 py-2 bg-[#ff0000] text-white rounded-lg hover:opacity-80 transition-opacity">
                      YouTube
                    </a>
                  )}
                  {profile.social.twitter && (
                    <a href={profile.social.twitter} target="_blank" rel="noopener noreferrer"
                       className="px-4 py-2 bg-black text-white rounded-lg hover:opacity-80 transition-opacity border border-gray-700">
                      X / Twitter
                    </a>
                  )}
                  {profile.social.instagram && (
                    <a href={profile.social.instagram} target="_blank" rel="noopener noreferrer"
                       className="px-4 py-2 bg-gradient-to-r from-[#833ab4] via-[#fd1d1d] to-[#fcb045] text-white rounded-lg hover:opacity-80 transition-opacity">
                      Instagram
                    </a>
                  )}
                </div>
              </div>
            )}

            {/* Play Me Online Panel */}
            {(profile.platforms?.webcam?.dartcounter || profile.platforms?.webcam?.wda ||
              profile.platforms?.webcam?.lidarts || profile.platforms?.webcam?.nakka ||
              profile.platforms?.webcam?.prodarter || profile.platforms?.webcam?.dartconnect ||
              profile.platforms?.webcam?.godartspro || profile.platforms?.autoScoring?.scolia ||
              profile.platforms?.autoScoring?.autodarts || profile.platforms?.autoScoring?.targetomni) && (
              <div className="card">
                <h2 className="text-lg font-semibold text-white mb-4">Play Me Online</h2>

                {/* Auto-Scoring Systems - Show as featured badges */}
                {(profile.platforms?.autoScoring?.scolia || profile.platforms?.autoScoring?.autodarts ||
                  profile.platforms?.autoScoring?.targetomni) && (
                  <div className="mb-4">
                    <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">Auto-Scoring</h3>
                    <div className="flex flex-wrap gap-2">
                      {profile.platforms?.autoScoring?.scolia && (
                        <span className="px-3 py-1.5 bg-[#1111d4]/20 border border-[#1111d4]/50 rounded-full text-sm font-medium text-[#4444ff]">
                          Scolia
                        </span>
                      )}
                      {profile.platforms?.autoScoring?.autodarts && (
                        <span className="px-3 py-1.5 bg-[#ff6b00]/20 border border-[#ff6b00]/50 rounded-full text-sm font-medium text-[#ff9500]">
                          Autodarts
                        </span>
                      )}
                      {profile.platforms?.autoScoring?.targetomni && (
                        <span className="px-3 py-1.5 bg-[#00a651]/20 border border-[#00a651]/50 rounded-full text-sm font-medium text-[#39b54a]">
                          Target Omni
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {/* DartCounter Featured Card */}
                {profile.platforms?.webcam?.dartcounter && (
                  <a
                    href={platformUrls.dartcounter(profile.platforms.webcam.dartcounter)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mb-4 flex items-center justify-center gap-4 bg-[#252550] hover:bg-[#2a2a5a] rounded-xl p-4 transition-all duration-300 group cursor-pointer"
                  >
                    <img
                      src={`https://api.qrserver.com/v1/create-qr-code/?size=80x80&data=${encodeURIComponent(platformUrls.dartcounter(profile.platforms.webcam.dartcounter))}`}
                      alt="DartCounter QR Code"
                      className="w-20 h-20 rounded-lg bg-white p-1"
                    />
                    <div className="text-left">
                      <p className="text-gray-500 text-xs uppercase tracking-wider mb-1">Find me on</p>
                      <p className="text-white font-bold text-lg group-hover:text-[#1111d4] transition-colors">DartCounter</p>
                      <p className="text-gray-500 text-sm">Scan or click to view profile</p>
                    </div>
                  </a>
                )}

                {/* Webcam/Manual Platforms */}
                {(profile.platforms?.webcam?.wda ||
                  profile.platforms?.webcam?.lidarts || profile.platforms?.webcam?.nakka ||
                  profile.platforms?.webcam?.prodarter || profile.platforms?.webcam?.dartconnect ||
                  profile.platforms?.webcam?.godartspro) && (
                  <div>
                    <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">Webcam / Manual Scoring</h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      {profile.platforms?.webcam?.wda && (
                        <div className="p-2 bg-[#252550] rounded-lg text-center">
                          <div className="text-xs text-gray-500">WDA</div>
                          <div className="text-sm text-white font-medium truncate">{profile.platforms.webcam.wda}</div>
                        </div>
                      )}
                      {profile.platforms?.webcam?.lidarts && (
                        <a
                          href={platformUrls.lidarts(profile.platforms.webcam.lidarts)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 bg-[#252550] hover:bg-[#2a2a5a] rounded-lg text-center transition-colors group"
                        >
                          <div className="text-xs text-gray-500 flex items-center justify-center gap-1">
                            Lidarts
                            <ArrowTopRightOnSquareIcon className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                          </div>
                          <div className="text-sm text-white font-medium truncate">{profile.platforms.webcam.lidarts}</div>
                        </a>
                      )}
                      {profile.platforms?.webcam?.nakka && (
                        <a
                          href={platformUrls.nakka(profile.platforms.webcam.nakka)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 bg-[#252550] hover:bg-[#2a2a5a] rounded-lg text-center transition-colors group"
                        >
                          <div className="text-xs text-gray-500 flex items-center justify-center gap-1">
                            N01
                            <ArrowTopRightOnSquareIcon className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                          </div>
                          <div className="text-sm text-white font-medium truncate">{profile.platforms.webcam.nakka}</div>
                        </a>
                      )}
                      {profile.platforms?.webcam?.prodarter && (
                        <a
                          href={platformUrls.prodarter(profile.platforms.webcam.prodarter)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 bg-[#252550] hover:bg-[#2a2a5a] rounded-lg text-center transition-colors group"
                        >
                          <div className="text-xs text-gray-500 flex items-center justify-center gap-1">
                            Pro-Darter
                            <ArrowTopRightOnSquareIcon className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                          </div>
                          <div className="text-sm text-white font-medium truncate">{profile.platforms.webcam.prodarter}</div>
                        </a>
                      )}
                      {profile.platforms?.webcam?.dartconnect && (
                        <div className="p-2 bg-[#252550] rounded-lg text-center">
                          <div className="text-xs text-gray-500">DartConnect</div>
                          <div className="text-sm text-white font-medium truncate">{profile.platforms.webcam.dartconnect}</div>
                        </div>
                      )}
                      {profile.platforms?.webcam?.godartspro && (
                        <div className="p-2 bg-[#252550] rounded-lg text-center">
                          <div className="text-xs text-gray-500">GoDartsPro</div>
                          <div className="text-sm text-white font-medium truncate">{profile.platforms.webcam.godartspro}</div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </section>
        )}

        {/* Schedule Section */}
        {(profile.layout === 'single' || activeSection === 'schedule') && (
          <section id="schedule" className="space-y-6">
            {/* Book a Game - Only for friends */}
            {isFriend && friendshipChecked && (
              <div className="card">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-white">Book a Game</h2>
                  <CalendarDaysIcon className="w-5 h-5 text-gray-500" />
                </div>
                <p className="text-sm text-gray-400 mb-4">
                  Schedule a match with {profile.displayName}
                </p>
                <Link
                  to={`/friends/${data.userId}/book`}
                  className="btn-primary inline-block text-center"
                >
                  Book a Game
                </Link>
              </div>
            )}

            {/* Not friends message */}
            {!isFriend && !isOwnProfile && friendshipChecked && isAuthenticated && (
              <div className="card">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-white">Book a Game</h2>
                  <CalendarDaysIcon className="w-5 h-5 text-gray-500" />
                </div>
                <p className="text-sm text-gray-400">
                  You need to be friends with {profile.displayName} to book a game.
                </p>
              </div>
            )}

            {/* Availability */}
            <div className="card">
              <h2 className="text-lg font-semibold text-white mb-4">Availability</h2>
              {Object.keys(slotsByDate).length === 0 ? (
                <p className="text-gray-500 text-center py-4">No availability set</p>
              ) : (
                <div className="space-y-4">
                  {Object.entries(slotsByDate).map(([dateKey, slots]) => (
                    <div key={dateKey}>
                      <h3 className="text-sm font-medium text-gray-400 mb-2">
                        {format(parseISO(dateKey), 'EEEE, MMMM d')}
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {slots.map((slot, i) => (
                          <span key={i} className="px-3 py-1 bg-[#252550] rounded-lg text-sm text-white">
                            {format(parseISO(slot.start), 'h:mm a')} - {format(parseISO(slot.end), 'h:mm a')}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Upcoming Games */}
            <div className="card">
              <h2 className="text-lg font-semibold text-white mb-4">Upcoming Matches</h2>
              {upcomingGames.length === 0 ? (
                <p className="text-gray-500 text-center py-4">No upcoming matches</p>
              ) : (
                <div className="space-y-3">
                  {upcomingGames.map((game, i) => (
                    <div key={i} className="flex items-center justify-between p-3 bg-[#252550] rounded-lg">
                      <div>
                        <p className="font-medium text-white">
                          {profile.displayName}
                          <span className="text-gray-400 mx-2">vs</span>
                          {game.opponentName}
                        </p>
                        <p className="text-sm text-gray-400">
                          {game.leagueName || 'Casual'} - {game.legs} legs
                        </p>
                      </div>
                      <div className="text-right text-sm text-gray-400">
                        <p>{format(parseISO(game.scheduledStart), 'MMM d')}</p>
                        <p>{format(parseISO(game.scheduledStart), 'h:mm a')}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Recent Results */}
            <div className="card">
              <h2 className="text-lg font-semibold text-white mb-4">Recent Results</h2>
              {recentResults.length === 0 ? (
                <p className="text-gray-500 text-center py-4">No recent results</p>
              ) : (
                <div className="space-y-3">
                  {recentResults.map((result, i) => (
                    <div key={i} className="flex items-center justify-between p-3 bg-[#252550] rounded-lg">
                      <div>
                        <p className="font-medium text-white">
                          {profile.displayName}
                          <span className="text-gray-400 mx-2">vs</span>
                          {result.opponentName}
                        </p>
                        <p className="text-sm text-gray-400">{result.leagueName || 'Casual'}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-white">
                          {result.ownerLegsWon} - {result.opponentLegsWon}
                        </p>
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          result.winner === 'owner' ? 'bg-green-900/50 text-green-300' :
                          result.winner === 'opponent' ? 'bg-red-900/50 text-red-300' :
                          'bg-yellow-900/50 text-yellow-300'
                        }`}>
                          {result.winner === 'owner' ? 'WIN' : result.winner === 'opponent' ? 'LOSS' : 'DRAW'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>
        )}

        {/* Leagues Section */}
        {(profile.layout === 'single' || activeSection === 'leagues') && (
          <section id="leagues" className="space-y-6">
            <div className="card">
              <h2 className="text-lg font-semibold text-white mb-4">Leagues</h2>
              {leagues.length === 0 ? (
                <p className="text-gray-500 text-center py-4">Not participating in any leagues</p>
              ) : (
                <div className="grid gap-4 md:grid-cols-2">
                  {leagues.map((league, i) => (
                    <div key={i} className="p-4 bg-[#252550] rounded-lg">
                      <div className="flex items-center gap-3 mb-3">
                        {league.logoUrl ? (
                          <img src={league.logoUrl} alt={league.name} className="w-12 h-12 rounded object-cover" />
                        ) : (
                          <div className="w-12 h-12 bg-[#1a1a3a] rounded flex items-center justify-center text-[#1111d4] font-bold">
                            {league.abbreviation || league.name.charAt(0)}
                          </div>
                        )}
                        <div>
                          <h3 className="font-semibold text-white">{league.name}</h3>
                          {league.abbreviation && (
                            <p className="text-sm text-gray-400">{league.abbreviation}</p>
                          )}
                        </div>
                        {league.isActive && (
                          <span className="ml-auto px-2 py-0.5 bg-green-900/30 text-green-300 rounded-full text-xs flex items-center gap-1">
                            <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                            Active
                          </span>
                        )}
                      </div>
                      <div className="flex gap-2">
                        {league.facebookUrl && (
                          <a href={league.facebookUrl} target="_blank" rel="noopener noreferrer"
                             className="p-2 bg-[#1877f2] text-white rounded hover:opacity-80 text-sm">
                            FB
                          </a>
                        )}
                        {league.discordUrl && (
                          <a href={league.discordUrl} target="_blank" rel="noopener noreferrer"
                             className="p-2 bg-[#5865f2] text-white rounded hover:opacity-80 text-sm">
                            Discord
                          </a>
                        )}
                        {league.whatsappUrl && (
                          <a href={league.whatsappUrl} target="_blank" rel="noopener noreferrer"
                             className="p-2 bg-[#25d366] text-white rounded hover:opacity-80 text-sm">
                            WhatsApp
                          </a>
                        )}
                        {league.websiteUrl && (
                          <a href={league.websiteUrl} target="_blank" rel="noopener noreferrer"
                             className="p-2 bg-[#1111d4] text-white rounded hover:opacity-80 text-sm">
                            Website
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>
        )}

        {/* Contact Section */}
        {(profile.layout === 'single' || activeSection === 'contact') && (profile.social.discord || profile.social.facebook) && (
          <section id="contact" className="space-y-6">
            <div className="card text-center">
              <h2 className="text-xl font-semibold text-white mb-4">Contact</h2>
              <p className="text-gray-400 mb-4">
                Get in touch with {profile.displayName}
              </p>
              <div className="flex justify-center gap-3">
                {profile.social.facebook && (
                  <a href={profile.social.facebook} target="_blank" rel="noopener noreferrer"
                     className="btn-primary">
                    Facebook
                  </a>
                )}
                {profile.social.discord && (
                  <span className="px-4 py-2 bg-[#5865f2] text-white rounded-lg">
                    Discord: {profile.social.discord}
                  </span>
                )}
              </div>
            </div>
          </section>
        )}

        {/* Footer */}
        <footer className="text-center text-sm text-gray-600 pt-8 border-t border-[#2a2a4a]">
          Powered by <Link to="/" className="text-[#1111d4] hover:underline">Darts Scheduler</Link>
        </footer>
      </div>

    </div>
  );
}
